import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, Network, Sigma } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as ss from 'simple-statistics';
import { ParsedWorksheet, WorksheetColumn } from './worksheet';

interface MultivariateAnalysisProps {
  worksheet: ParsedWorksheet;
  selectedColumnId?: string;
}

type ScalingMode = 'standardize' | 'center';

type PCAResult = {
  rowIndices: number[];
  variableNames: string[];
  means: number[];
  scales: number[];
  standardizedRows: number[][];
  eigenvalues: number[];
  explainedVariance: number[];
  cumulativeVariance: number[];
  components: number[][];
  scores: number[][];
  correlationMatrix: number[][];
};

type ClusterSummary = {
  cluster: number;
  size: number;
  share: number;
  centroidStandardized: number[];
  centroidOriginal: number[];
};

type KMeansResult = {
  assignments: number[];
  iterations: number;
  inertia: number;
  summaries: ClusterSummary[];
};

const COLORS = ['#7c3aed', '#0ea5e9', '#f97316', '#22c55e', '#ef4444', '#eab308'];

const formatNumber = (value: number, digits = 4) => (Number.isFinite(value) ? value.toFixed(digits) : '—');

const dot = (a: number[], b: number[]) => a.reduce((sum, value, index) => sum + value * b[index], 0);
const magnitude = (vector: number[]) => Math.sqrt(dot(vector, vector));
const normalizeVector = (vector: number[]) => {
  const size = magnitude(vector);
  if (!Number.isFinite(size) || size === 0) return vector.map(() => 0);
  return vector.map((value) => value / size);
};

const matrixVectorMultiply = (matrix: number[][], vector: number[]) =>
  matrix.map((row) => row.reduce((sum, value, index) => sum + value * vector[index], 0));

const outerProduct = (a: number[], b: number[]) =>
  a.map((aValue) => b.map((bValue) => aValue * bValue));

const subtractMatrices = (a: number[][], b: number[][]) =>
  a.map((row, rowIndex) => row.map((value, columnIndex) => value - b[rowIndex][columnIndex]));

const distanceSquared = (a: number[], b: number[]) =>
  a.reduce((sum, value, index) => sum + (value - b[index]) ** 2, 0);

const columnColor = (value: number) => {
  const clamped = Math.max(-1, Math.min(1, value));
  if (clamped >= 0) {
    const strength = clamped;
    return `rgba(59, 130, 246, ${0.15 + strength * 0.55})`;
  }
  return `rgba(239, 68, 68, ${0.15 + Math.abs(clamped) * 0.55})`;
};

const buildUsableRows = (columns: WorksheetColumn[]) => {
  if (columns.length === 0) return { rowIndices: [] as number[], matrix: [] as number[][] };

  const rowCount = columns[0]?.values.length ?? 0;
  const rowIndices: number[] = [];
  const matrix: number[][] = [];

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const vector = columns.map((column) => Number(column.values[rowIndex] ?? ''));
    if (vector.every((value) => Number.isFinite(value))) {
      rowIndices.push(rowIndex + 1);
      matrix.push(vector);
    }
  }

  return { rowIndices, matrix };
};

const standardizeRows = (rows: number[][], mode: ScalingMode) => {
  if (rows.length === 0) {
    return { standardizedRows: [] as number[][], means: [] as number[], scales: [] as number[] };
  }

  const featureCount = rows[0].length;
  const means = Array.from({ length: featureCount }, (_, index) => ss.mean(rows.map((row) => row[index])));
  const spreads = Array.from({ length: featureCount }, (_, index) => {
    const sample = rows.map((row) => row[index]);
    const stdev = sample.length > 1 ? ss.sampleStandardDeviation(sample) : 0;
    if (mode === 'center') return 1;
    return Number.isFinite(stdev) && stdev > 1e-8 ? stdev : 1;
  });

  return {
    means,
    scales: spreads,
    standardizedRows: rows.map((row) => row.map((value, index) => (value - means[index]) / spreads[index])),
  };
};

const computeCorrelationMatrix = (rows: number[][]) => {
  if (rows.length === 0) return [] as number[][];
  const featureCount = rows[0].length;
  return Array.from({ length: featureCount }, (_, rowIndex) =>
    Array.from({ length: featureCount }, (_, columnIndex) => {
      const a = rows.map((row) => row[rowIndex]);
      const b = rows.map((row) => row[columnIndex]);
      if (rowIndex === columnIndex) return 1;
      const correlation = ss.sampleCorrelation(a, b);
      return Number.isFinite(correlation) ? correlation : 0;
    }),
  );
};

const computeCovarianceMatrix = (rows: number[][]) => {
  if (rows.length <= 1) return [] as number[][];
  const featureCount = rows[0].length;
  return Array.from({ length: featureCount }, (_, rowIndex) =>
    Array.from({ length: featureCount }, (_, columnIndex) => {
      const covariance = rows.reduce((sum, row) => sum + row[rowIndex] * row[columnIndex], 0) / (rows.length - 1);
      return Number.isFinite(covariance) ? covariance : 0;
    }),
  );
};

const powerIteration = (matrix: number[][], maxIterations = 200) => {
  const dimension = matrix.length;
  let vector = normalizeVector(Array.from({ length: dimension }, (_, index) => 1 + index / Math.max(1, dimension)));

  for (let iteration = 0; iteration < maxIterations; iteration += 1) {
    const next = matrixVectorMultiply(matrix, vector);
    const normalized = normalizeVector(next);
    const delta = Math.sqrt(normalized.reduce((sum, value, index) => sum + (value - vector[index]) ** 2, 0));
    vector = normalized;
    if (delta < 1e-8) break;
  }

  const mv = matrixVectorMultiply(matrix, vector);
  const eigenvalue = dot(vector, mv);
  return {
    eigenvalue: Number.isFinite(eigenvalue) ? eigenvalue : 0,
    eigenvector: vector,
  };
};

const computePca = (rows: number[][], rowIndices: number[], variableNames: string[], mode: ScalingMode): PCAResult | null => {
  if (rows.length < 3 || variableNames.length < 2) return null;

  const { standardizedRows, means, scales } = standardizeRows(rows, mode);
  const covarianceMatrix = computeCovarianceMatrix(standardizedRows);
  if (covarianceMatrix.length === 0) return null;

  let workingMatrix = covarianceMatrix.map((row) => [...row]);
  const maxComponents = Math.min(variableNames.length, 4);
  const eigenvalues: number[] = [];
  const components: number[][] = [];

  for (let componentIndex = 0; componentIndex < maxComponents; componentIndex += 1) {
    const { eigenvalue, eigenvector } = powerIteration(workingMatrix);
    if (!Number.isFinite(eigenvalue) || eigenvalue <= 1e-8) break;

    eigenvalues.push(eigenvalue);
    components.push(eigenvector);
    workingMatrix = subtractMatrices(
      workingMatrix,
      outerProduct(eigenvector, eigenvector).map((row) => row.map((value) => value * eigenvalue)),
    );
  }

  if (components.length === 0) return null;

  const totalVariance = eigenvalues.reduce((sum, value) => sum + value, 0) || 1;
  const explainedVariance = eigenvalues.map((value) => (value / totalVariance) * 100);
  const cumulativeVariance = explainedVariance.map((_, index) => explainedVariance.slice(0, index + 1).reduce((sum, value) => sum + value, 0));
  const scores = standardizedRows.map((row) => components.map((component) => dot(row, component)));

  return {
    rowIndices,
    variableNames,
    means,
    scales,
    standardizedRows,
    eigenvalues,
    explainedVariance,
    cumulativeVariance,
    components,
    scores,
    correlationMatrix: computeCorrelationMatrix(rows),
  };
};

const runKMeans = (rows: number[][], means: number[], scales: number[], requestedClusters: number): KMeansResult | null => {
  if (rows.length < 3 || rows[0]?.length < 2) return null;

  const clusterCount = Math.max(2, Math.min(requestedClusters, rows.length));
  let centroids = Array.from({ length: clusterCount }, (_, index) => rows[Math.floor((index * rows.length) / clusterCount)] ?? rows[0]).map((row) => [...row]);
  let assignments = Array.from({ length: rows.length }, () => 0);
  let iterations = 0;

  for (; iterations < 30; iterations += 1) {
    const nextAssignments = rows.map((row) => {
      let bestCluster = 0;
      let bestDistance = Number.POSITIVE_INFINITY;
      centroids.forEach((centroid, centroidIndex) => {
        const distance = distanceSquared(row, centroid);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestCluster = centroidIndex;
        }
      });
      return bestCluster;
    });

    const unchanged = nextAssignments.every((cluster, index) => cluster === assignments[index]);
    assignments = nextAssignments;

    const updatedCentroids = centroids.map((centroid, centroidIndex) => {
      const members = rows.filter((_, rowIndex) => assignments[rowIndex] === centroidIndex);
      if (members.length === 0) return centroid;
      return centroid.map((_, featureIndex) => ss.mean(members.map((member) => member[featureIndex])));
    });

    centroids = updatedCentroids;
    if (unchanged) break;
  }

  const inertia = rows.reduce((sum, row, rowIndex) => sum + distanceSquared(row, centroids[assignments[rowIndex]]), 0);
  const summaries = centroids.map((centroid, centroidIndex) => {
    const members = rows.filter((_, rowIndex) => assignments[rowIndex] === centroidIndex);
    return {
      cluster: centroidIndex,
      size: members.length,
      share: rows.length === 0 ? 0 : (members.length / rows.length) * 100,
      centroidStandardized: centroid,
      centroidOriginal: centroid.map((value, featureIndex) => value * scales[featureIndex] + means[featureIndex]),
    } satisfies ClusterSummary;
  });

  return {
    assignments,
    iterations: iterations + 1,
    inertia,
    summaries,
  };
};

export const MultivariateAnalysis: React.FC<MultivariateAnalysisProps> = ({ worksheet, selectedColumnId }) => {
  const numericCandidates = worksheet.numericColumns.filter((column) => column.nonEmptyCount >= 3);
  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([]);
  const [scalingMode, setScalingMode] = useState<ScalingMode>('standardize');
  const [clusterCount, setClusterCount] = useState('3');

  useEffect(() => {
    setSelectedColumnIds((current) => {
      const valid = current.filter((columnId) => numericCandidates.some((column) => column.id === columnId));
      if (valid.length >= 2) return valid;

      const prioritized = [selectedColumnId, ...numericCandidates.map((column) => column.id)].filter(Boolean) as string[];
      const unique = Array.from(new Set(prioritized));
      return unique.slice(0, Math.min(4, numericCandidates.length));
    });
  }, [numericCandidates, selectedColumnId]);

  const selectedColumns = useMemo(
    () => numericCandidates.filter((column) => selectedColumnIds.includes(column.id)).slice(0, 8),
    [numericCandidates, selectedColumnIds],
  );

  const usable = useMemo(() => buildUsableRows(selectedColumns), [selectedColumns]);
  const pca = useMemo(
    () => computePca(usable.matrix, usable.rowIndices, selectedColumns.map((column) => column.name), scalingMode),
    [scalingMode, selectedColumns, usable.matrix, usable.rowIndices],
  );

  const clustering = useMemo(() => {
    if (!pca) return null;
    return runKMeans(pca.standardizedRows, pca.means, pca.scales, Number(clusterCount) || 3);
  }, [clusterCount, pca]);

  const pcScoreRows = useMemo(() => {
    if (!pca) return [] as Array<{ rowIndex: number; pc1: number; pc2: number; cluster: string }>;
    return pca.scores.map((score, index) => ({
      rowIndex: pca.rowIndices[index],
      pc1: score[0] ?? 0,
      pc2: score[1] ?? 0,
      cluster: `Cluster ${(clustering?.assignments[index] ?? 0) + 1}`,
    }));
  }, [clustering?.assignments, pca]);

  const varianceRows = useMemo(() => {
    if (!pca) return [] as Array<{ component: string; explained: number; cumulative: number }>;
    return pca.explainedVariance.map((value, index) => ({
      component: `PC${index + 1}`,
      explained: value,
      cumulative: pca.cumulativeVariance[index] ?? value,
    }));
  }, [pca]);

  const loadingRows = useMemo(() => {
    if (!pca) return [] as Array<{ variable: string; pc1: number; pc2: number; contribution: number }>;
    return pca.variableNames.map((variable, index) => ({
      variable,
      pc1: pca.components[0]?.[index] ?? 0,
      pc2: pca.components[1]?.[index] ?? 0,
      contribution: Math.abs(pca.components[0]?.[index] ?? 0) + Math.abs(pca.components[1]?.[index] ?? 0),
    })).sort((a, b) => b.contribution - a.contribution);
  }, [pca]);

  const clusterScatterGroups = useMemo(() => {
    const groups = new Map<string, Array<{ rowIndex: number; pc1: number; pc2: number }>>();
    pcScoreRows.forEach((row) => {
      if (!groups.has(row.cluster)) groups.set(row.cluster, []);
      groups.get(row.cluster)?.push({ rowIndex: row.rowIndex, pc1: row.pc1, pc2: row.pc2 });
    });
    return Array.from(groups.entries());
  }, [pcScoreRows]);

  const assignmentPreview = useMemo(() => {
    if (!clustering) return [] as Array<{ rowIndex: number; cluster: string; pc1: number; pc2: number }>;
    return pcScoreRows.slice(0, 12).map((row, index) => ({
      rowIndex: row.rowIndex,
      cluster: `Cluster ${(clustering.assignments[index] ?? 0) + 1}`,
      pc1: row.pc1,
      pc2: row.pc2,
    }));
  }, [clustering, pcScoreRows]);

  const toggleColumn = (columnId: string) => {
    setSelectedColumnIds((current) => {
      if (current.includes(columnId)) {
        return current.length <= 2 ? current : current.filter((id) => id !== columnId);
      }
      if (current.length >= 8) return current;
      return [...current, columnId];
    });
  };

  if (numericCandidates.length < 2) {
    return (
      <Alert className="border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Multivariate analysis needs more numeric data</AlertTitle>
        <AlertDescription>
          MapleStats needs at least two numeric columns with usable values before PCA or clustering can run.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">ready</Badge>
                <Badge variant="outline">Multivariate workspace</Badge>
              </div>
              <CardTitle className="mt-3 flex items-center gap-2 text-3xl">
                <Network className="h-8 w-8 text-teal-600" />
                Multivariate Analysis
              </CardTitle>
              <CardDescription className="mt-2 max-w-3xl text-base">
                Explore multivariate structure with PCA, variable relationships, and clustering from the active worksheet.
              </CardDescription>
            </div>
            <div className="grid min-w-[15rem] grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="text-muted-foreground">Usable rows</div>
                <div className="mt-1 text-2xl font-semibold">{usable.matrix.length}</div>
              </div>
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="text-muted-foreground">Variables</div>
                <div className="mt-1 text-2xl font-semibold">{selectedColumns.length}</div>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Setup</CardTitle>
          <CardDescription>Select two to eight numeric worksheet columns to profile together.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Variables</Label>
            <div className="flex flex-wrap gap-2">
              {numericCandidates.map((column) => {
                const isSelected = selectedColumnIds.includes(column.id);
                return (
                  <Button
                    key={column.id}
                    type="button"
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleColumn(column.id)}
                    className="max-w-full"
                  >
                    <span className="truncate">{column.name}</span>
                  </Button>
                );
              })}
            </div>
            <div className="text-xs text-muted-foreground">
              PCA and clustering work best with continuous variables on similar scales. Standardization is recommended for mixed-unit data.
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Scaling</Label>
              <Select value={scalingMode} onValueChange={(value: ScalingMode) => setScalingMode(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standardize">Standardize (z-score)</SelectItem>
                  <SelectItem value="center">Center only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Clusters</Label>
              <Select value={clusterCount} onValueChange={setClusterCount}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2, 3, 4, 5, 6].map((count) => (
                    <SelectItem key={count} value={String(count)}>
                      {count} clusters
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedColumns.length < 2 || usable.matrix.length < 3 || !pca ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Not enough complete multivariate cases yet</AlertTitle>
          <AlertDescription>
            Choose at least two numeric columns and make sure at least three rows have values across every selected variable.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
            <Card>
              <CardHeader>
                <CardTitle>Principal component score map</CardTitle>
                <CardDescription>Rows projected onto the first two principal components.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 12, right: 20, left: 0, bottom: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" dataKey="pc1" name="PC1" tickFormatter={(value) => Number(value).toFixed(2)} />
                      <YAxis type="number" dataKey="pc2" name="PC2" tickFormatter={(value) => Number(value).toFixed(2)} />
                      <Tooltip
                        formatter={(value: number) => Number(value).toFixed(4)}
                        labelFormatter={(_, payload) => {
                          const point = payload?.[0]?.payload as { rowIndex?: number; cluster?: string } | undefined;
                          return point ? `Row ${point.rowIndex} · ${point.cluster}` : 'Observation';
                        }}
                      />
                      <Legend />
                      {clusterScatterGroups.map(([cluster, rows], index) => (
                        <Scatter key={cluster} name={cluster} data={rows} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Explained variance</CardTitle>
                <CardDescription>How much structure each principal component captures.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={varianceRows} margin={{ top: 12, right: 12, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="component" />
                      <YAxis tickFormatter={(value) => `${Number(value).toFixed(0)}%`} />
                      <Tooltip formatter={(value: number) => `${Number(value).toFixed(2)}%`} />
                      <Legend />
                      <Bar dataKey="explained" fill="#14b8a6" name="Explained" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="cumulative" fill="#7c3aed" name="Cumulative" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">PC1 variance</div>
                    <div className="mt-1 text-2xl font-semibold">{formatNumber(pca.explainedVariance[0] ?? 0, 2)}%</div>
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-muted-foreground">PC1 + PC2</div>
                    <div className="mt-1 text-2xl font-semibold">{formatNumber((pca.explainedVariance[0] ?? 0) + (pca.explainedVariance[1] ?? 0), 2)}%</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <Card>
              <CardHeader>
                <CardTitle>Variable loadings</CardTitle>
                <CardDescription>Variables driving separation in the first two components.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Variable</TableHead>
                      <TableHead className="text-right">PC1</TableHead>
                      <TableHead className="text-right">PC2</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingRows.map((row) => (
                      <TableRow key={row.variable}>
                        <TableCell className="font-medium">{row.variable}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.pc1)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.pc2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Correlation matrix</CardTitle>
                <CardDescription>Pairwise linear relationships across selected variables.</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <div className="min-w-[32rem]">
                  <div className="grid" style={{ gridTemplateColumns: `12rem repeat(${pca.variableNames.length}, minmax(5rem, 1fr))` }}>
                    <div className="border-b border-r p-2 text-sm font-medium text-muted-foreground">Variable</div>
                    {pca.variableNames.map((name) => (
                      <div key={`head-${name}`} className="border-b border-r p-2 text-center text-xs font-medium text-muted-foreground">
                        {name}
                      </div>
                    ))}
                    {pca.variableNames.map((rowName, rowIndex) => (
                      <React.Fragment key={rowName}>
                        <div className="border-b border-r p-2 text-sm font-medium">{rowName}</div>
                        {pca.variableNames.map((columnName, columnIndex) => {
                          const value = pca.correlationMatrix[rowIndex]?.[columnIndex] ?? 0;
                          return (
                            <div
                              key={`${rowName}-${columnName}`}
                              className="border-b border-r p-2 text-center text-sm font-medium"
                              style={{ backgroundColor: columnColor(value) }}
                            >
                              {formatNumber(value, 3)}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {clustering && (
            <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
              <Card>
                <CardHeader>
                  <CardTitle>Cluster summary</CardTitle>
                  <CardDescription>K-means grouping on the selected standardized variables.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-xl border bg-muted/30 p-3">
                      <div className="text-muted-foreground">Clusters</div>
                      <div className="mt-1 text-2xl font-semibold">{clustering.summaries.length}</div>
                    </div>
                    <div className="rounded-xl border bg-muted/30 p-3">
                      <div className="text-muted-foreground">Iterations</div>
                      <div className="mt-1 text-2xl font-semibold">{clustering.iterations}</div>
                    </div>
                    <div className="rounded-xl border bg-muted/30 p-3">
                      <div className="text-muted-foreground">Inertia</div>
                      <div className="mt-1 text-2xl font-semibold">{formatNumber(clustering.inertia, 2)}</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {clustering.summaries.map((summary, index) => (
                      <div key={summary.cluster} className="rounded-xl border p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                            <div className="font-medium">Cluster {summary.cluster + 1}</div>
                          </div>
                          <Badge variant="outline">{summary.size} rows · {formatNumber(summary.share, 1)}%</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Cluster centroids</CardTitle>
                  <CardDescription>Centroid positions translated back to the original worksheet scale.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cluster</TableHead>
                        {selectedColumns.map((column) => (
                          <TableHead key={column.id} className="text-right">{column.name}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clustering.summaries.map((summary) => (
                        <TableRow key={summary.cluster}>
                          <TableCell className="font-medium">Cluster {summary.cluster + 1}</TableCell>
                          {summary.centroidOriginal.map((value, index) => (
                            <TableCell key={`${summary.cluster}-${selectedColumns[index]?.id ?? index}`} className="text-right">
                              {formatNumber(value)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {clustering && (
            <Card>
              <CardHeader>
                <CardTitle>Assignment preview</CardTitle>
                <CardDescription>First rows mapped into PCA space and cluster membership.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Cluster</TableHead>
                      <TableHead className="text-right">PC1</TableHead>
                      <TableHead className="text-right">PC2</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignmentPreview.map((row) => (
                      <TableRow key={row.rowIndex}>
                        <TableCell>{row.rowIndex}</TableCell>
                        <TableCell>{row.cluster}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.pc1)}</TableCell>
                        <TableCell className="text-right">{formatNumber(row.pc2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Alert>
            <Sigma className="h-4 w-4" />
            <AlertTitle>What this multivariate release includes</AlertTitle>
            <AlertDescription>
              This first release focuses on PCA, variable structure, and k-means clustering. It creates a strong immediate demo while leaving room for later discriminant, factor, and correspondence workflows.
            </AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
};
